import { getAllEnumValues } from '@upright/common/utils';
import { format as d3format } from 'd3-format';
import { AndAssessmentPriorization, AssessmentPrioritization, ByLanguage, MetricNature } from '../types';
import {
  EnumPaiAssessment,
  EnumPaiMetric,
  enumPaiMetrics,
  NaceCode,
  NumericPaiAssessment,
  numericPaiMetrics,
  PaiMetric,
  PolicyStatus,
  PositiveIndicatorValue,
  PositivePaiMetric,
} from './types';

export const natureToCmp: { [key in MetricNature]: number } = {
  [MetricNature.DIRECT_DISCLOSURE]: 4,
  [MetricNature.THIRD_PARTY_ASSESSMENT]: 3,
  [MetricNature.UPRIGHT_MANUAL_ASSESSMENT]: 2,
  [MetricNature.UPRIGHT_ESTIMATE]: 1,
};

interface SortableAssessment {
  id: number;
  year: number;
  source?: string;
  createdAt: string;
  nature: MetricNature;
  isInactive: boolean;
  value: number | null;
}

/**
 * Given multiple assessments, figure out which one to use (intended use: figure out which assessment to use for a
 * given company and metric)
 *
 * Logic:
 *
 * * Ignore disabled candidates
 * * Prefer assessments in the order of direct disclosure, third party assessment, upright assessment
 * * Between candidates of equal natures, prefer the later reported year
 * * Between candidates of equal natures and reported years, prefer the one that was created later
 * * Between candidates of equal natrues, reported years and creation timestamps, return the one that appears first in
 *   the list
 *
 * @param candidates list of candidate assessments between which to choose
 */
export function selectAssessment<T extends SortableAssessment>(
  candidates: T[],
  requireYear: number | undefined,
  requireSource: string | undefined,
): T | undefined {
  const eligibleCandidates = candidates
    .filter(c => c.value !== null)
    .filter(c => !c.isInactive)
    .filter(c => !requireYear || c.year === requireYear)
    .filter(c => !requireSource || c.source === requireSource);

  if (eligibleCandidates.length === 0) {
    return undefined;
  }
  return sortCandidates(eligibleCandidates)[0];
}

export function resolveAssessments<T extends SortableAssessment>(
  assessments: T[],
  requireYear: number | undefined,
  requireSource: string | undefined,
): Array<T & AndAssessmentPriorization> {
  if (assessments.length === 0) {
    return [];
  }
  const chosen = selectAssessment(assessments, requireYear, requireSource);
  const resolved: Array<T & AndAssessmentPriorization> = assessments.map((a: T) => ({
    ...a,
    state: resolveState(a, chosen?.id),
  }));
  return sortCandidates(resolved);
}

export function resolveState<T extends SortableAssessment>(candidate: T, chosenId?: number) {
  if (candidate.isInactive) {
    return AssessmentPrioritization.INGORED;
  }
  if (candidate.id === chosenId) {
    return AssessmentPrioritization.SELECTED;
  }
  return AssessmentPrioritization.OTHER;
}

function sortCandidates<T extends SortableAssessment>(candidates: T[]) {
  candidates.sort((a, b) => {
    const cmpA = natureToCmp[a.nature] * a.year;
    const cmpB = natureToCmp[b.nature] * b.year;
    if (cmpA !== cmpB) {
      return cmpB - cmpA;
    }
    return a.createdAt > b.createdAt ? -1 : 1;
  });
  return candidates;
}

export function friendlyMetricName(metric: PaiMetric) {
  return paiNamesByLanguage.en![metric];
}

export const paiNamesByLanguage: Partial<ByLanguage<Record<PaiMetric, string>>> = {
  en: {
    [PaiMetric.GHG1]: 'Scope 1 GHG emissions',
    [PaiMetric.GHG2]: 'Scope 2 GHG emissions',
    [PaiMetric.GHG3]: 'Scope 3 GHG emissions',
    [PaiMetric.GHGF]: 'Carbon footprint (Scope 1, 2 and 3)',
    [PaiMetric.GHGF12]: 'Carbon footprint (Scope 1 and 2)',
    [PaiMetric.GHGI]: 'GHG intensity (Scope 1, 2 and 3)',
    [PaiMetric.GHGI12]: 'GHG intensity (Scope 1 and 2)',
    [PaiMetric.AIFF]: 'Fossil fuel sector activity',
    [PaiMetric.NRNW]: 'Non-renewable energy share',
    [PaiMetric.HICS]: 'High impact climate sector',
    [PaiMetric.TECO]: 'Total energy consumption',
    [PaiMetric.NRGI]: 'Energy consumption intensity',
    [PaiMetric.BIOD]: 'Activities negatively affecting biodiversity-sensitive areas',
    [PaiMetric.WTRE]: 'Emissions to water',
    [PaiMetric.HAZW]: 'Hazardous waste',
    [PaiMetric.SOCV]: 'UNGC/OECD norm violations',
    [PaiMetric.SOCP]: 'UNGC/OECD compliance mechanisms',
    [PaiMetric.PAYG]: 'Unadjusted gender pay gap',
    [PaiMetric.FMBR]: 'Board gender diversity',
    [PaiMetric.CVWP]: 'Involvement in controversial weapons',
    [PaiMetric.CHEM]: 'Production of chemicals',
    [PaiMetric.HRSP]: 'Human rights policy',
    [PaiMetric.ACBP]: 'Anti-corruption / anti-bribery policies',
    [PaiMetric.WTRU]: 'Water usage',
    // [PaiMetric.WTRR]: 'Water recycling',
  },
  // TODO translate and use in api/metrics/legend
  // fi: {},
  // sv: {},
};

export function metricUnit(metric: PaiMetric) {
  switch (metric) {
    case PaiMetric.GHG1:
    case PaiMetric.GHG3:
    case PaiMetric.GHGF:
    case PaiMetric.GHGF12:
    case PaiMetric.GHG2:
      return 'tons of CO2e';
    case PaiMetric.GHGI:
    case PaiMetric.GHGI12:
      return 'tons of CO2e / €M';
    case PaiMetric.NRNW:
      return '%';
    case PaiMetric.TECO:
      return 'MWh';
    case PaiMetric.NRGI:
      return 'GWh / €M';
    case PaiMetric.WTRE:
      return 'tons';
    case PaiMetric.WTRU:
      return 'cubic meters';
    // case PaiMetric.WTRR:
    //   return '%';
    case PaiMetric.HAZW:
      return 'tons';
    case PaiMetric.PAYG:
      return '%';
    case PaiMetric.FMBR:
      return '%';
    case PaiMetric.HICS:
      return '';
    default:
      throw new Error(`No unit defined for metric ${metric}`);
  }
}

interface RenderablePaiValue {
  value: number;
  metric: PaiMetric;
}
export function renderValue(candidate: RenderablePaiValue) {
  if (numericPaiMetrics.has(candidate.metric)) {
    return renderNumericValue(candidate as RenderableNumericPaiValue);
  }
  if (enumPaiMetrics.has(candidate.metric)) {
    return renderEnumValue(candidate as RenderableEnumPaiValue);
  }
  switch (candidate.metric as PositivePaiMetric) {
    case PaiMetric.AIFF:
    case PaiMetric.SOCV:
    case PaiMetric.BIOD:
    case PaiMetric.CVWP:
    case PaiMetric.CHEM:
      return candidate.value === PositiveIndicatorValue.NO ? 'No' : 'Yes';
    case PaiMetric.SOCP:
      return candidate.value === PositiveIndicatorValue.NO ? 'No policies found' : 'Policies found';
  }
}

type RenderableNumericPaiValue = Pick<NumericPaiAssessment, 'value' | 'metric'>;
function renderNumericValue(candidate: RenderableNumericPaiValue) {
  const format = formatOfNumericMetric(candidate.value);
  const formatted = d3format(format)(candidate.value);
  return `${formatted} ${metricUnit(candidate.metric)}`;
}

type RenderableEnumPaiValue = Pick<EnumPaiAssessment, 'value' | 'metric'>;
function renderEnumValue(candidate: RenderableEnumPaiValue) {
  switch (candidate.metric) {
    case PaiMetric.HICS:
      return renderHicsCode(candidate.value as NaceCode);
    case PaiMetric.ACBP:
    case PaiMetric.HRSP: {
      switch (candidate.value) {
        case PolicyStatus.STATED_POLICY:
          return 'Policies found';
        case PolicyStatus.MENTION_ONLY:
          return 'No policies found (topic mentioned)';
        case PolicyStatus.NO_MENTIONS:
          return 'No policies found';
        default:
          throw Error(`Invalid value ${candidate.value} for metric ${candidate.metric}`);
      }
    }
  }
}

export function renderHicsCode(code: NaceCode) {
  switch (code) {
    case NaceCode.A:
      return 'Agriculture, forestry and fishing';
    case NaceCode.B:
      return 'Mining and quarrying';
    case NaceCode.C:
      return 'Manufacturing';
    case NaceCode.D:
      return 'Electricity, gas, steam and air conditioning supply';
    case NaceCode.E:
      return 'Water supply; sewerage, waste management and remediation activities';
    case NaceCode.F:
      return 'Construction';
    case NaceCode.G:
      return 'Wholesale and retail trade; repair of motor vehicles and motorcycles';
    case NaceCode.H:
      return 'Transportation and storage';
    case NaceCode.I:
      return 'Accommodation and food service activities';
    case NaceCode.J:
      return 'Information and communication';
    case NaceCode.K:
      return 'Financial and insurance activities';
    case NaceCode.L:
      return 'Real estate activities';
    case NaceCode.M:
      return 'Professional, scientific and technical activities';
    case NaceCode.N:
      return 'Administrative and support service activities';
    case NaceCode.O:
      return 'Public administration and defence; compulsory social security 84';
    case NaceCode.P:
      return 'Education';
    case NaceCode.Q:
      return 'Human health and social work activities';
    case NaceCode.R:
      return 'Arts, entertainment and recreation';
    case NaceCode.S:
      return 'Other service activities';
    case NaceCode.T:
      return 'Activities of households as employers; undifferentiated goods- and services-producing activities of households for own use';
    case NaceCode.U:
      return 'Activities of extraterritorial organisations and bodies';
    case NaceCode.NONE:
      return 'None';
    default:
      return `Invalid NACE code ${code as string}`;
  }
}

export function naceCodeToString(code: NaceCode) {
  switch (code) {
    case NaceCode.A:
      return 'A';
    case NaceCode.B:
      return 'B';
    case NaceCode.C:
      return 'C';
    case NaceCode.D:
      return 'D';
    case NaceCode.E:
      return 'E';
    case NaceCode.F:
      return 'F';
    case NaceCode.G:
      return 'G';
    case NaceCode.H:
      return 'H';
    case NaceCode.I:
      return 'I';
    case NaceCode.J:
      return 'J';
    case NaceCode.K:
      return 'K';
    case NaceCode.L:
      return 'L';
    case NaceCode.M:
      return 'M';
    case NaceCode.N:
      return 'N';
    case NaceCode.O:
      return 'O';
    case NaceCode.P:
      return 'P';
    case NaceCode.Q:
      return 'Q';
    case NaceCode.R:
      return 'R';
    case NaceCode.S:
      return 'S';
    case NaceCode.T:
      return 'T';
    case NaceCode.U:
      return 'U';
    case NaceCode.NONE:
      return 'NONE';
    default:
      throw new Error(`Unknown NACE code ${code as string}`);
  }
}

function formatOfNumericMetric(value: number) {
  if (value >= 10) {
    return ',.0f'; // Trim decimals
  }
  if (value < 0.001) {
    return ',.0f'; // Snap very small values to 0
  }
  if (value < 1) {
    return ',.2~r'; // Show two significant decimals
  }
  return ',.1~f'; // One fixed decimal
}

export function getDefaultPaiValue(metric: PaiMetric) {
  if (metric === PaiMetric.HICS) {
    return NaceCode.A;
  }
  return 0;
}

export function getEnumPaiValues(metric: EnumPaiMetric): number[] {
  switch (metric) {
    case PaiMetric.HICS:
      return getAllEnumValues(NaceCode, 'number');
    case PaiMetric.ACBP:
    case PaiMetric.HRSP:
      return getAllEnumValues(PolicyStatus, 'number');
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unexpected Enum PAI metric "${metric}"`);
  }
}
